import { Button, Paper, Typography } from "@material-ui/core";
import React, { useCallback, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { usePaystackPayment } from "react-paystack";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStateContext } from "../../context/StateContext";
import { tabView } from "../../global/responsive";

// Styled Components
const Container = styled.div`
  margin: 30px auto !important;
  width: 60%;
  padding: 10px !important;
  ${tabView({ width: "80% !important" })};
`;

const CheckoutPaper = styled(Paper)`
  padding: 25px 20px !important;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 10px 0px !important;
  gap: 15px;
`;

const CheckoutButton = styled(Button)`
  color: #b8a06a !important;
  background: black !important;
  &:disabled {
    color: #000 !important;
    background: #b8a06a !important;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  font-size: 14px;
  margin-top: 8px;
`;

// Constants
const SHIPPING_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT - Abuja",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
  "Outside Nigeria",
];

const DELIVERY_OPTIONS = [
  { type: "Select Delivery", disabled: true },
  { type: "Pickup (Available in Lagos and Ibadan Only)", disabled: false },
  { type: "Home Delivery", disabled: false },
];

const PICKUP_LOCATIONS = [
  { name: "Select Pickup", disabled: true },
  { name: "Head Office: University of Lagos", disabled: false },
  { name: "Sub Office: Kuola Road , Ibadan", disabled: false },
];

// Delivery fee mapping
const DELIVERY_FEE_MAP = {
  1700: 1700,
  2500: 2500,
  4000: 4000,
  5000: 5000,
  10000: 10000,
};

// Initial user info state
const INITIAL_USER_INFO = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  emailAddress: "",
  address: "",
  city: "",
  state: "",
  deliveryType: "",
  deliveryChoice: "",
  marketerCode: "",
};

const CheckoutForm = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(INITIAL_USER_INFO);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paystackDisabled, setPaystackDisabled] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [shippingFee, setShippingFee] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [pickupLocation, setPickupLocation] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [touchedFields, setTouchedFields] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const {
    totalPrice,
    setCartItems,
    setTotalPrice,
    setTotalQuantities,
    cartItems,
    taxAndVat,
    deliveryPrices,
  } = useStateContext();

  // Validation logic
  const validateField = useCallback(
    (fieldName, value) => {
      const errors = {};

      if (fieldName === "firstName" || fieldName === "lastName") {
        if (!value || value.trim().length < 2) {
          errors[fieldName] = "Name must be at least 2 characters";
        }
      }

      if (fieldName === "phoneNumber") {
        if (!value || value.trim().length < 10) {
          errors[fieldName] = "Phone number must be at least 10 digits";
        }
      }

      if (fieldName === "emailAddress") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
          errors[fieldName] = "Please enter a valid email address";
        }
      }

      if (fieldName === "address") {
        if (!value || value.trim().length < 5) {
          errors[fieldName] = "Address must be at least 5 characters";
        }
      }

      if (fieldName === "city") {
        if (!value || value.trim().length < 2) {
          errors[fieldName] = "City must be at least 2 characters";
        }
      }

      if (fieldName === "state") {
        if (!value) {
          errors[fieldName] = "Please select a state";
        }
      }

      if (fieldName === "deliveryType") {
        if (!value) {
          errors[fieldName] = "Please select a delivery type";
        }
      }

      if (fieldName === "deliveryChoice") {
        if (!value) {
          errors[fieldName] =
            userInfo.deliveryType === "Home Delivery"
              ? "Please select a shipping option"
              : "Please select a pickup location";
        }
      }

      return errors;
    },
    [userInfo.deliveryType]
  );

  // Memoized final total price
  const finalTotalPrice = useMemo(
    () => totalPrice + taxAndVat,
    [totalPrice, taxAndVat]
  );

  // Memoized config for Paystack
  const [config, setConfig] = useState({
    reference: new Date().getTime().toString(),
    firstname: userInfo.firstName,
    lastname: userInfo.lastName,
    phone: userInfo.phoneNumber,
    email: userInfo.emailAddress || "",
    amount: 0,
    publicKey: process.env.REACT_APP_KAIZEN_BRAND_PUBLIC_KEY,
    metadata: {
      orderId: `kaiz${new Date().getTime().toString()}`,
      cartItems: cartItems,
    },
  });

  const initializePayment = usePaystackPayment(config);

  // Handle user info changes with validation
  const handleUserInfoChange = useCallback(
    (field, value) => {
      setUserInfo((prev) => ({ ...prev, [field]: value }));
      setTouchedFields((prev) => ({ ...prev, [field]: true }));

      // Validate the field
      const errors = validateField(field, value);
      if (Object.keys(errors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...errors }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [validateField]
  );

  // Handle marketer code input with validation
  const handleMarketerInputChange = useCallback((event) => {
    const { value } = event.target;
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;

    if (value.length > 6) {
      setErrorMessage("Maximum 6 alphanumeric characters allowed");
      setUserInfo((prev) => ({
        ...prev,
        marketerCode: value.slice(0, 6),
      }));
    } else if (value && !alphanumericRegex.test(value)) {
      setErrorMessage("Only alphanumeric characters allowed");
    } else {
      setErrorMessage("");
      setUserInfo((prev) => ({ ...prev, marketerCode: value }));
    }
  }, []);

  // Validate entire form
  const validateEntireForm = useCallback(() => {
    const allErrors = {};

    // Validate all required fields
    const fieldsToValidate = [
      "firstName",
      "lastName",
      "phoneNumber",
      "emailAddress",
      "address",
      "city",
      "state",
      "deliveryType",
      "deliveryChoice",
    ];

    fieldsToValidate.forEach((field) => {
      const fieldErrors = validateField(field, userInfo[field]);
      if (Object.keys(fieldErrors).length > 0) {
        allErrors[field] = fieldErrors[field];
      }
    });

    return allErrors;
  }, [userInfo, validateField]);

  // Handle payment success
  const onPaymentSuccess = useCallback(() => {
    setCartItems([]);
    setTotalQuantities(0);
    setTotalPrice(0);
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.clear();
    }
    navigate("/");
  }, [setCartItems, setTotalQuantities, setTotalPrice, navigate]);

  // Handle payment close
  const onPaymentClose = useCallback(() => {
    navigate("/checkout");
    setPaystackDisabled(false);
  }, [navigate]);

  // Format cart items for backend
  const formatCartItemsForBackend = useCallback(() => {
    return cartItems.map((item) => ({
      productId: item._id, // Backend reference ID
      productName: item.productName,
      productPrice: item.productPrice, // Individual item price
      productImages: item.productImages, // Image array
      quantity: item.quantity,
      color: item.color,
      colorName: item.colorName,
      size: item.size,
      type: item.type,
      length: item.len,
    }));
  }, [cartItems]);

  // Handle home delivery submission
  const handleHomeDeliverySubmit = useCallback(() => {
    const deliveryFee = DELIVERY_FEE_MAP[userInfo.deliveryChoice];
    if (!deliveryFee) return false;

    const priceInPaystack = finalTotalPrice * 100;
    const totalAmount = priceInPaystack + deliveryFee * 100;
    const formattedCartItems = formatCartItemsForBackend();

    setShippingFee(deliveryFee);
    setIsSubmitting(true);
    setPaystackDisabled(false);

    setConfig((prev) => ({
      ...prev,
      email: userInfo.emailAddress,
      amount: totalAmount,
      metadata: {
        ...prev.metadata,
        firstname: userInfo.firstName,
        lastname: userInfo.lastName,
        phone: userInfo.phoneNumber,
        email: userInfo.emailAddress,
        cartItems: formattedCartItems,
        subTotal: finalTotalPrice,
        totalPrice: finalTotalPrice + deliveryFee,
        shippingFee: deliveryFee,
        address: `${userInfo.address} ${userInfo.city} ${userInfo.state}`,
        marketerCode: userInfo.marketerCode,
        deliveryType: userInfo.deliveryType,
      },
    }));

    return true;
  }, [userInfo, finalTotalPrice, formatCartItemsForBackend]);

  // Handle pickup submission
  const handlePickupSubmit = useCallback(() => {
    if (!userInfo.deliveryChoice) return false;

    setPickupLocation(userInfo.deliveryChoice);
    setIsSubmitting(true);
    setPaystackDisabled(false);

    const priceInPaystack = finalTotalPrice * 100;

    setConfig((prev) => ({
      ...prev,
      email: userInfo.emailAddress,
      amount: priceInPaystack,
      metadata: {
        ...prev.metadata,
        firstname: userInfo.firstName,
        lastname: userInfo.lastName,
        phone: userInfo.phoneNumber,
        email: userInfo.emailAddress,
        cartItems: cartItems,
        subTotal: finalTotalPrice,
        totalPrice: finalTotalPrice,
        pickupLocation: userInfo.deliveryChoice,
        address: `${userInfo.address} ${userInfo.city} ${userInfo.state}`,
        deliveryType: userInfo.deliveryType,
      },
    }));

    return true;
  }, [userInfo, finalTotalPrice, cartItems]);

  // Handle form submission
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      // Validate entire form before submission
      const allErrors = validateEntireForm();

      if (Object.keys(allErrors).length > 0) {
        setFormErrors(allErrors);
        setTouchedFields({
          firstName: true,
          lastName: true,
          phoneNumber: true,
          emailAddress: true,
          address: true,
          city: true,
          state: true,
          deliveryType: true,
          deliveryChoice: true,
        });
        return;
      }

      if (userInfo.deliveryType === "Home Delivery") {
        handleHomeDeliverySubmit();
      } else if (
        userInfo.deliveryType === "Pickup (Available in Lagos and Ibadan Only)"
      ) {
        handlePickupSubmit();
      }
    },
    [
      userInfo.deliveryType,
      handleHomeDeliverySubmit,
      handlePickupSubmit,
      validateEntireForm,
    ]
  );

  // Render delivery choice options based on delivery type
  const renderDeliveryChoice = useMemo(() => {
    switch (userInfo.deliveryType) {
      case "Pickup (Available in Lagos and Ibadan Only)":
        return (
          <Form.Select
            aria-label="Select Pickup Location"
            className="mt-1 p-2"
            value={userInfo.deliveryChoice || ""}
            onChange={(e) =>
              handleUserInfoChange("deliveryChoice", e.target.value)
            }
            required
          >
            <option value="">Select Pickup Location</option>
            {PICKUP_LOCATIONS.filter((loc) => !loc.disabled).map((location) => (
              <option key={location.name} value={location.name}>
                {location.name}
              </option>
            ))}
          </Form.Select>
        );

      case "Home Delivery":
        return (
          <Form.Select
            aria-label="Select Delivery Option"
            className="mt-1 p-2"
            value={userInfo.deliveryChoice || ""}
            onChange={(e) =>
              handleUserInfoChange("deliveryChoice", e.target.value)
            }
            required
          >
            <option value="">Select Shipping Option</option>
            {deliveryPrices.map((fee) => (
              <option
                key={fee?.priceName}
                value={String(fee?.priceAmount || "")}
                disabled={fee?.disabled}
              >
                {`${fee?.priceName} ${
                  fee?.priceAmount ? `₦${fee.priceAmount}` : ""
                }`}
              </option>
            ))}
          </Form.Select>
        );

      default:
        return null;
    }
  }, [
    userInfo.deliveryType,
    userInfo.deliveryChoice,
    deliveryPrices,
    handleUserInfoChange,
  ]);

  return (
    <Container>
      <CheckoutPaper variant="outlined">
        <Typography variant="h4" className="mb-3" align="center">
          Checkout
        </Typography>
        <Form onSubmit={handleSubmit}>
          {/* Name Fields */}
          <Row className="gap-4 mt-2">
            <Col>
              <Form.Control
                className="p-2"
                placeholder="First name"
                type="text"
                required
                value={userInfo.firstName}
                onChange={(e) =>
                  handleUserInfoChange("firstName", e.target.value)
                }
                isInvalid={touchedFields.firstName && !!formErrors.firstName}
              />
              {touchedFields.firstName && formErrors.firstName && (
                <ErrorMessage>{formErrors.firstName}</ErrorMessage>
              )}
            </Col>
            <Col>
              <Form.Control
                className="p-2"
                placeholder="Last name"
                type="text"
                required
                value={userInfo.lastName}
                onChange={(e) =>
                  handleUserInfoChange("lastName", e.target.value)
                }
                isInvalid={touchedFields.lastName && !!formErrors.lastName}
              />
              {touchedFields.lastName && formErrors.lastName && (
                <ErrorMessage>{formErrors.lastName}</ErrorMessage>
              )}
            </Col>
          </Row>

          {/* Contact Fields */}
          <Row className="gap-4 mt-2">
            <Col>
              <Form.Control
                className="p-2"
                placeholder="Phone Number"
                type="tel"
                required
                value={userInfo.phoneNumber}
                onChange={(e) =>
                  handleUserInfoChange("phoneNumber", e.target.value)
                }
                isInvalid={
                  touchedFields.phoneNumber && !!formErrors.phoneNumber
                }
              />
              {touchedFields.phoneNumber && formErrors.phoneNumber && (
                <ErrorMessage>{formErrors.phoneNumber}</ErrorMessage>
              )}
            </Col>
            <Col>
              <Form.Control
                className="p-2"
                placeholder="Email"
                type="email"
                required
                value={userInfo.emailAddress}
                onChange={(e) =>
                  handleUserInfoChange("emailAddress", e.target.value)
                }
                isInvalid={
                  touchedFields.emailAddress && !!formErrors.emailAddress
                }
              />
              {touchedFields.emailAddress && formErrors.emailAddress && (
                <ErrorMessage>{formErrors.emailAddress}</ErrorMessage>
              )}
            </Col>
          </Row>

          {/* Address Fields */}
          <Form.Control
            className="mt-2 p-2"
            placeholder="Address"
            type="text"
            required
            value={userInfo.address}
            onChange={(e) => handleUserInfoChange("address", e.target.value)}
            isInvalid={touchedFields.address && !!formErrors.address}
          />
          {touchedFields.address && formErrors.address && (
            <ErrorMessage>{formErrors.address}</ErrorMessage>
          )}

          {/* City and State */}
          <Row className="gap-4 mt-2">
            <Col>
              <Form.Control
                className="p-2"
                placeholder="City"
                type="text"
                required
                value={userInfo.city}
                onChange={(e) => handleUserInfoChange("city", e.target.value)}
                isInvalid={touchedFields.city && !!formErrors.city}
              />
              {touchedFields.city && formErrors.city && (
                <ErrorMessage>{formErrors.city}</ErrorMessage>
              )}
            </Col>
            <Col>
              <Form.Select
                aria-label="Select State"
                className="p-2"
                required
                value={userInfo.state || ""}
                onChange={(e) => handleUserInfoChange("state", e.target.value)}
                isInvalid={touchedFields.state && !!formErrors.state}
              >
                <option value="">Select State</option>
                {SHIPPING_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </Form.Select>
              {touchedFields.state && formErrors.state && (
                <ErrorMessage>{formErrors.state}</ErrorMessage>
              )}
            </Col>
          </Row>

          {/* Delivery Type */}
          <label htmlFor="deliveryType" className="mt-2">
            Delivery Type
          </label>
          <Form.Select
            id="deliveryType"
            aria-label="Select Delivery Type"
            className="mt-1 p-2"
            required
            value={userInfo.deliveryType || ""}
            onChange={(e) => {
              handleUserInfoChange("deliveryType", e.target.value);
              handleUserInfoChange("deliveryChoice", "");
            }}
            isInvalid={touchedFields.deliveryType && !!formErrors.deliveryType}
          >
            <option value="">Select Delivery Type</option>
            {DELIVERY_OPTIONS.filter((opt) => !opt.disabled).map((option) => (
              <option key={option.type} value={option.type}>
                {option.type}
              </option>
            ))}
          </Form.Select>
          {touchedFields.deliveryType && formErrors.deliveryType && (
            <ErrorMessage>{formErrors.deliveryType}</ErrorMessage>
          )}

          {/* Conditional Delivery Choice */}
          {userInfo.deliveryType && (
            <>
              <label htmlFor="deliveryChoice" className="mt-2">
                {userInfo.deliveryType === "Home Delivery"
                  ? "Shipping Option"
                  : "Pickup Location"}
              </label>
              <div>
                {renderDeliveryChoice}
                {touchedFields.deliveryChoice && formErrors.deliveryChoice && (
                  <ErrorMessage>{formErrors.deliveryChoice}</ErrorMessage>
                )}
              </div>
            </>
          )}

          {/* Marketer Code */}
          <Form.Control
            className="mt-2 p-2"
            placeholder="Marketer's Code (Optional)"
            type="text"
            value={userInfo.marketerCode}
            onChange={handleMarketerInputChange}
          />
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          {/* Buttons */}
          <ButtonsContainer>
            <CheckoutButton
              className="mt-3 p-2"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitting}
            >
              Submit
            </CheckoutButton>
            <CheckoutButton
              className="mt-3 p-2"
              variant="contained"
              color="primary"
              disabled={paystackDisabled}
              onClick={() => {
                initializePayment(onPaymentSuccess, onPaymentClose);
                setPaystackDisabled((prev) => !prev);
              }}
            >
              Pay
            </CheckoutButton>
          </ButtonsContainer>
        </Form>
      </CheckoutPaper>
    </Container>
  );
};

export default CheckoutForm;
